'use client';

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Typography } from '../../../components';
import { useTranslations } from '../../../contexts/translation.context';
import { FiltersSection, TransactionsTable } from './components';
import { TransactionData, TransactionsFilters, TransactionsFiltersProps } from './types';
import { transformTransactionData } from './utils';
import {
  DEFAULT_FILTERS,
  DEFAULT_PAGE_SIZE,
  SEARCH_DEBOUNCE_DELAY,
  MODELS_PAGE_SIZE,
  MODEL_SEARCH_DEBOUNCE_DELAY,
} from './constants';
import { clientStatisticService } from '../../../api';
import clientAccService from '../../../api/client/clientAccService';
import { usePageBreadcrumbs } from '../../../hooks/usePageBreadcrumbs';

// API call to fetch transactions using service
const fetchTransactions = async (params: any) => {
  try {
    const response = await clientStatisticService.getTransactions(params);
    return response.data;
  } catch (error) {
    console.error('Error fetching transactions:', error);
    throw error;
  }
};

const Transactions: React.FC = () => {
  const { t } = useTranslations();
  const [transactions, setTransactions] = useState<TransactionData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);
  const [transactionTotal, setTransactionTotal] = useState<any>(null);
  const [filters, setFilters] = useState<TransactionsFilters>(DEFAULT_FILTERS);
  const breadcrumbs = useMemo(
    () => [
      { label: t('sidebar.dashboard'), href: '/' },
      { label: t('sidebar.transactions'), isActive: true },
    ],
    [t]
  );
  usePageBreadcrumbs(breadcrumbs);
  // Models and accounts state
  const [availableModels, setAvailableModels] = useState<
    { id: string; name: string; username: string; avatar?: string }[]
  >([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelSearchTerm, setModelSearchTerm] = useState('');

  const [availableAccounts, setAvailableAccounts] = useState<
    { id: string; name: string; username: string; avatar?: string }[]
  >([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountSearchTerm, setAccountSearchTerm] = useState('');

  // Local states for debounced inputs
  const [localSearch, setLocalSearch] = useState(filters.search);
  const [localId, setLocalId] = useState(filters.id || '');
  const [localCompanyId, setLocalCompanyId] = useState(filters.companyId || '');

  const loadModels = useCallback(async (searchTerm: string = '') => {
    setModelsLoading(true);
    try {
      const response = await clientAccService.getAccounts({
        page: 1,
        page_size: MODELS_PAGE_SIZE,
        search: searchTerm.trim() || undefined,
      });
      const resTotal = await clientStatisticService.getTransactionsOwn();
      setTransactionTotal(resTotal.data);
      if (
        response &&
        response.data &&
        response.data.objects &&
        Array.isArray(response.data.objects)
      ) {
        const models = response.data.objects.map((account: any) => ({
          id: account.id,
          name: account.name,
          username: account.of_user_name,
          avatar: account.avatar,
        }));
        setAvailableModels(models);
      } else {
        setAvailableModels([]);
      }
    } catch (error) {
      setAvailableModels([]);
    } finally {
      setModelsLoading(false);
    }
  }, []);

  const loadAccounts = useCallback(async (searchTerm: string = '') => {
    setAccountsLoading(true);
    try {
      const response = await clientAccService.getAccounts({
        page: 1,
        page_size: MODELS_PAGE_SIZE,
        search: searchTerm.trim() || undefined,
      });

      if (
        response &&
        response.data &&
        response.data.objects &&
        Array.isArray(response.data.objects)
      ) {
        const accounts = response.data.objects.map((account: any) => ({
          id: account.id,
          name: account.name,
          username: account.of_user_name,
          avatar: account.avatar,
        }));
        setAvailableAccounts(accounts);
      } else {
        setAvailableAccounts([]);
      }
    } catch (error) {
      setAvailableAccounts([]);
    } finally {
      setAccountsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!modelSearchTerm.trim()) {
      loadModels();
      return;
    }

    const timer = setTimeout(() => {
      loadModels(modelSearchTerm);
    }, MODEL_SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [modelSearchTerm, loadModels]);

  useEffect(() => {
    if (!accountSearchTerm.trim()) {
      loadAccounts();
      return;
    }

    const timer = setTimeout(() => {
      loadAccounts(accountSearchTerm);
    }, MODEL_SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [accountSearchTerm, loadAccounts]);

  // Debounce for search input
  useEffect(() => {
    if (localSearch === filters.search) return;

    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: localSearch }));
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [localSearch, filters.search]);

  // Debounce for ID input
  useEffect(() => {
    if ((localId || null) === (filters.id ?? null)) return;

    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, id: localId || null }));
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [localId, filters.id]);

  // Debounce for Company ID input
  useEffect(() => {
    if ((localCompanyId || null) === (filters.companyId ?? null)) return;

    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, companyId: localCompanyId || null }));
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [localCompanyId, filters.companyId]);

  // Load transactions when dependencies change
  useEffect(() => {
    const loadTransactions = async () => {
      setIsLoading(true);
      try {
        const apiParams = {
          page: currentPage,
          page_size: pageSize,
          search: filters.search || undefined,
          id: filters.id || undefined,
          model_id: filters.models.length > 0 ? filters.models[0] : filters.modelId || undefined,
          created_at_start: filters.createdAtStart || undefined,
          created_at_end: filters.createdAtEnd || undefined,
          of_id: filters.ofId || undefined,
          of_company_id: filters.ofCompanyId || undefined,
          company_id: filters.companyId || undefined,
          of_model_id: filters.ofModelId || undefined,
          models: filters.models.length > 0 ? filters.models : undefined,
          accounts: filters.accounts.length > 0 ? filters.accounts : undefined,
          status: filters.status || undefined,
        };

        const response = await fetchTransactions(apiParams);

        if (response && response.objects && Array.isArray(response.objects)) {
          const transformedTransactions = response.objects.map(transformTransactionData);
          setTransactions(transformedTransactions);
          setTotalCount(response.count || 0);
        } else {
          setTransactions([]);
          setTotalCount(0);
        }
      } catch (error) {
        console.error('Failed to load transactions:', error);
        setTransactions([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    };

    loadTransactions();
  }, [currentPage, pageSize, filters]);

  const filteredTransactions = useMemo(() => {
    let result = transactions;

    if (filters.search) {
      result = result.filter((transaction) => {
        const searchLower = filters.search.toLowerCase();
        return (
          transaction.id.toLowerCase().includes(searchLower) ||
          transaction.model.name.toLowerCase().includes(searchLower) ||
          transaction.model.ofUserName.toLowerCase().includes(searchLower) ||
          transaction.subscriber.name.toLowerCase().includes(searchLower) ||
          transaction.subscriber.username.toLowerCase().includes(searchLower)
        );
      });
    }

    return result;
  }, [transactions, filters.search]);

  const handleFilterChange = useCallback(
    (key: keyof TransactionsFilters, value: string | string[] | number | null) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
      setCurrentPage(1);
    },
    []
  );

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  }, []);

  const handleRefresh = useCallback(() => {
    // Trigger a reload by updating a dependency
    setCurrentPage((prev) => prev);
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setLocalSearch('');
    setLocalId('');
    setLocalCompanyId('');
    setCurrentPage(1);
  }, []);

  // Проверяем, есть ли активные фильтры
  const hasActiveFilters = useMemo(() => {
    return (
      filters.search !== '' ||
      filters.id !== null ||
      filters.companyId !== null ||
      filters.createdAtStart !== null ||
      filters.createdAtEnd !== null ||
      filters.models.length > 0 ||
      filters.accounts.length > 0
    );
  }, [filters]);

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="px-4 py-6 md:py-4 md:px-6">
        <Typography variant="h5" className="md:font-semibold text-gray-900">
          {t('transactions.title')}
        </Typography>
      </div>

      <FiltersSection
        filters={filters}
        localSearch={localSearch}
        localId={localId}
        localCompanyId={localCompanyId}
        availableModels={availableModels}
        modelsLoading={modelsLoading}
        availableAccounts={availableAccounts}
        accountsLoading={accountsLoading}
        onSearchChange={setLocalSearch}
        onIdChange={setLocalId}
        onCompanyIdChange={setLocalCompanyId}
        onFilterChange={handleFilterChange}
        onModelSearchChange={setModelSearchTerm}
        onAccountSearchChange={setAccountSearchTerm}
        onRefresh={handleRefresh}
        isRefreshing={isLoading}
        hasActiveFilters={hasActiveFilters}
        onResetFilters={handleResetFilters}
      />

      {/* Table */}
      <div className="px-4 md:px-6 md:h-full">
        <TransactionsTable
          transactionTotal={transactionTotal}
          transactions={filteredTransactions}
          isLoading={isLoading}
          totalCount={totalCount}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>
    </div>
  );
};

export default Transactions;
